import { useCallback, useRef } from "react";

/** Any body-write response carries the fresh optimistic-CAS token. */
type HasVersion = { manual: { version: number } };
/** The minimal shape of a TanStack mutation we enqueue (call-level onSuccess/onError are additive to the hook-level seed). */
type MutationLike<TVars, TData> = { mutate: (vars: TVars, opts: { onSuccess: (data: TData) => void; onError: (err: unknown) => void }) => void };

/**
 * Serializes EVERY manual-body write (step autosave + rename/type/payload + outline ops + media attach) through ONE queue
 * with a SINGLE shared CAS version — the fix for the Phase-10 autosave concurrency bugs (panel #210):
 *
 *  1. **No sibling self-409.** Two step-cards (or a card + an outline op) whose writes fire in the same debounce window used
 *     to each send their OWN pinned base version and race `manual.version` — the second deterministically 409'd
 *     `MANUAL_BODY_STALE` and its draft was discarded. Now each queued write runs AFTER the previous and reads the version
 *     the previous write LEFT, so same-user sequential edits never conflict with each other.
 *  2. **Failed writes reject.** `run` rejects its caller on mutation error, so the flush barrier can BLOCK a status
 *     transition / editor exit instead of the old `onSettled`-resolves-regardless false success that let those boundaries
 *     proceed with an unsaved (conflicted) edit.
 *
 * Version model (the per-card base-version, lifted to the editor + serialized):
 *  - `known` = our best server version, seeded from the load and advanced ONLY by our own successful writes.
 *  - While FULLY CLEAN + IDLE we reconcile `known` UP to the live query version (absorb a background / cross-user refetch or a
 *    post-transition bump), so a later structural op uses the fresh version.
 *  - While any field is DIRTY or a write is in flight we FREEZE `known` — a genuinely concurrent (cross-user) edit therefore
 *    still fails the CAS (→ the Refresh banner), never a silent clobber. (Transitions don't bump `version`, so a review
 *    action never falsely staled a following body edit.)
 */
export function useBodyWriteGate(liveVersion: number, isRecoverableFailure?: (err: unknown) => boolean) {
  const known = useRef(liveVersion);
  /**
   * The body our most recent write committed — the correct diff BASELINE for the next write in the chain
   * (ADR-053 D1). A caller that needs before/after otherwise has only its render-time closure, which is the
   * pre-write cache for every write after the first in a burst.
   *
   * **Used only while its version still matches `known`** (D3). Our own write leaves them equal; the clean+idle
   * reconcile below, a `reseed`, or any future path that advances `known` leaves them unequal and this retires
   * itself. Clearing it at each such site instead would work only for the sites someone remembered — and the
   * reconcile path is exactly the one nobody did.
   */
  const lastCommitted = useRef<HasVersion | undefined>(undefined);
  const dirty = useRef(new Set<number>());
  const inFlight = useRef(0);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  // Every currently-unsettled caller promise (text + structural + attach) — so {@link settle} can await the WHOLE queue and
  // block a boundary on ANY unsaved write, not just step-text (panel diff-#210 High #1).
  const pending = useRef<Set<Promise<unknown>>>(new Set());
  // A STICKY unacknowledged-failure latch: a rejected write is deleted from `pending` on settle, so awaiting only `pending`
  // would forget it and let a later boundary barrier resolve to a false green (panel diff-a1 High). It latches ONLY for
  // failures that leave unsaved divergence needing recovery (a stale-family 409 → the Refresh banner → `reseed`; or a
  // transient/unknown error with no banner → self-heals on the next successful write). A SURFACED, non-divergent structural
  // rejection (name taken / last section) does NOT latch — its own dismissible banner acknowledges it and the section simply
  // keeps its prior value, so it must never wedge the exit / status-transition barrier (panel diff-a1-round2 Medium).
  const unacked = useRef(false);
  // The current recoverable-failure classifier, kept in a ref so `run` stays identity-stable across renders (the editor
  // passes one that returns true for the surfaced structural codes). Absent → every failure is treated as blocking.
  const recoverable = useRef(isRecoverableFailure);
  recoverable.current = isRecoverableFailure;

  // Reconcile up while clean + idle (never down — versions are monotonic). Mutating a ref in render is intentional here (the
  // same pattern the cards used for their `versionRef`) and safe: no rendered output derives from it.
  if (dirty.current.size === 0 && inFlight.current === 0 && liveVersion > known.current) known.current = liveVersion;

  /** A field/row began diverging from its saved value — freeze the shared version so a concurrent edit still 409s. */
  const markDirty = useCallback((id: number) => {
    dirty.current.add(id);
  }, []);
  /** A field/row is back in sync (saved, reset, or unmounted) — once nothing is dirty the version can reconcile again. */
  const markClean = useCallback((id: number) => {
    dirty.current.delete(id);
  }, []);
  /** After a conflict-refresh: drop the frozen dirty session, and (once the authoritative version is known) force `known` to
   *  it — so a card that went dirty during the sub-second refetch window can't keep the stale token frozen (panel diff-#210). */
  const reseed = useCallback((version?: number) => {
    dirty.current.clear();
    unacked.current = false; // the refresh acknowledges + clears any prior write failure
    if (version != null) known.current = version;
  }, []);
  /** Acknowledge a DISCRETE self-surfaced action's failure (an edit-image / collage save that has its OWN error UI): clear
   *  ONLY the exit-barrier latch, leaving the shared dirty-set + version UNTOUCHED (diff-panel R3-r4-#1). `reseed()` clears the
   *  WHOLE dirty-set — reserved for an authoritative conflict-refresh — so using it here would unfreeze OTHER cards' unsaved
   *  text buffers and let a later flush absorb a concurrent cross-user version → silent lost update. */
  const ackFailure = useCallback(() => {
    unacked.current = false;
  }, []);

  /**
   * Enqueue a body write. The callback receives the CAS `expectedVersion` to send and must resolve the returned body (or
   * reject with the error). Writes run strictly one-at-a-time; each advances the shared version on success. Rejections
   * propagate to THIS caller while the queue keeps draining (a failed write must not wedge the editor).
   */
  const run = useCallback(<T extends HasVersion>(op: (expectedVersion: number, previous?: T) => Promise<T>): Promise<T> => {
    inFlight.current += 1;
    const exec = chain.current.then(async () => {
      // D3: the stored body is the previous write's ONLY while nothing else has moved `known` since. A version
      // mismatch means a refetch or a reseed intervened, and the caller's own cache is then the fresher truth.
      const carried = lastCommitted.current;
      const previous = carried !== undefined && carried.manual.version === known.current ? (carried as T) : undefined;
      const res = await op(known.current, previous);
      known.current = res.manual.version;
      lastCommitted.current = res;
      return res;
    });
    chain.current = exec.then(
      () => undefined,
      () => undefined,
    );
    const caller = exec.finally(() => {
      inFlight.current -= 1;
    });
    // Track for the barrier; drop when it settles (either way). The extra handler also marks the rejection handled so the
    // barrier's `allSettled` observing it later doesn't surface an unhandled rejection.
    pending.current.add(caller);
    caller.then(
      () => {
        pending.current.delete(caller);
        unacked.current = false; // the queue recovered — a success acknowledges/heals any prior transient failure (mechanism A)
      },
      (err: unknown) => {
        pending.current.delete(caller);
        // Latch ONLY a divergence-bearing failure; a surfaced structural rejection (recoverable) leaves nothing unsaved and
        // must not block the boundary barrier (else a name-conflict wedges exit/transitions until reload — diff-a1-round2).
        if (!recoverable.current?.(err)) unacked.current = true;
      },
    );
    return caller;
  }, []);

  /** Barrier: reject if any write failed and hasn't been acknowledged (the sticky latch), then await EVERY currently-queued
   *  write and reject if any of THOSE failed — so an editor exit / status transition blocks on any unsaved body write, then
   *  the Refresh banner surfaces the conflict (panel diff-#210 + diff-a1). */
  const settle = useCallback(async () => {
    if (unacked.current) throw new Error("BODY_WRITE_FAILED");
    const results = await Promise.allSettled([...pending.current]);
    // DIRTINESS is checked AFTER the drain (diff-panel 2b #2 + gate-panel High #2): the boolean latch above self-heals
    // on ANY later success ("mechanism A"), so a structural write the editor fires right after a FAILED autosave used to
    // clear it while the step's text was still unsaved — and a keystroke landing DURING a barrier-triggered retry
    // re-dirties the card after that retry's success. In both cases the card is still DIRTY here (its `.then` ran before
    // ours — `dirty` is current), and a dirty buffer after the drain means the newest text is NOT on the server: the
    // barrier stays red rather than let a transition fire and the debounce save into a now-frozen manual. Nothing is
    // attributed per owner: a card that unmounted already `markClean`'d (and never re-dirties once unmounted — see
    // StepCard), so a late rejection of its unmount flush only latches the self-healing boolean; view switches await
    // this barrier BEFORE unmounting, so that path carries no unsaved text (gate-panel rounds 1 + 2).
    if (results.some((r) => r.status === "rejected") || dirty.current.size > 0) throw new Error("BODY_WRITE_FAILED");
  }, []);

  /** Convenience: enqueue a TanStack mutation as a serialized write. `vars(expectedVersion)` builds the payload with the
   *  gate-supplied CAS token; the returned promise resolves the seeded body or rejects with the mutation error. */
  const enqueue = useCallback(
    <TVars, TData extends HasVersion>(
      mutation: MutationLike<TVars, TData>,
      vars: (expectedVersion: number, previous?: TData) => TVars,
    ): Promise<TData> =>
      run<TData>((expectedVersion, previous) =>
        new Promise<TData>((resolve, reject) =>
          mutation.mutate(vars(expectedVersion, previous), { onSuccess: resolve, onError: reject }),
        ),
      ),
    [run],
  );

  /**
   * Is there work that would be LOST if this editor lost the lock right now (diff-panel R8-F3)?
   *
   * Dirty buffers, writes in flight, or a latched failure. Reported on every heartbeat as "active", which is how the
   * ADR's rule — *never auto-release while unsaved or failed writes exist* — is actually enforced: the server cannot
   * see a client-side buffer, so only the client can answer this. It was documented as the mechanism and then never
   * wired, so an idle author with a FAILED autosave would have had the manual handed away underneath them.
   */
  const hasOutstandingWork = useCallback((): boolean => dirty.current.size > 0 || inFlight.current > 0 || unacked.current, []);

  return { run, enqueue, settle, markDirty, markClean, reseed, ackFailure, hasOutstandingWork };
}

export type BodyWriteGate = ReturnType<typeof useBodyWriteGate>;
